import { User } from '../types/database.types'

export type UserRole = 'user' | 'admin' | 'super_admin' | 'manager' | 'member'

export interface RolePermissions {
  canManageUsers: boolean
  canManageGroups: boolean
  canManageCurriculum: boolean
  canManageEvents: boolean
  canManageTasks: boolean
  canViewAnalytics: boolean
  canManageSystem: boolean
}

export const ROLE_HIERARCHY: Record<UserRole, number> = {
  user: 1,
  member: 2,
  manager: 3,
  admin: 4,
  super_admin: 5
}

export const DEFAULT_ROLE_PERMISSIONS: Record<UserRole, RolePermissions> = {
  user: {
    canManageUsers: false,
    canManageGroups: false,
    canManageCurriculum: false,
    canManageEvents: false,
    canManageTasks: true,
    canViewAnalytics: false,
    canManageSystem: false
  },
  member: {
    canManageUsers: false,
    canManageGroups: false,
    canManageCurriculum: false,
    canManageEvents: false,
    canManageTasks: true,
    canViewAnalytics: false,
    canManageSystem: false
  },
  manager: {
    canManageUsers: false,
    canManageGroups: true,
    canManageCurriculum: true,
    canManageEvents: true,
    canManageTasks: true,
    canViewAnalytics: true,
    canManageSystem: false
  },
  admin: {
    canManageUsers: true,
    canManageGroups: true,
    canManageCurriculum: true,
    canManageEvents: true,
    canManageTasks: true,
    canViewAnalytics: true,
    canManageSystem: false
  },
  super_admin: {
    canManageUsers: true,
    canManageGroups: true,
    canManageCurriculum: true,
    canManageEvents: true,
    canManageTasks: true,
    canViewAnalytics: true,
    canManageSystem: true
  }
}

export function getUserRole(user: User | null): UserRole {
  if (!user) return 'user'
  
  // Check for super admin first
  if (user.role === 'super_admin') return 'super_admin'
  
  // Check for admin
  if (user.role === 'admin') return 'admin'
  
  // Check for manager
  if (user.role === 'manager') return 'manager'
  
  // Check for member
  if (user.role === 'member') return 'member'
  
  // Default to user
  return 'user'
}

export function hasRole(user: User | null, requiredRole: UserRole): boolean {
  const userRole = getUserRole(user)
  return ROLE_HIERARCHY[userRole] >= ROLE_HIERARCHY[requiredRole]
}

export function hasPermission(user: User | null, permission: keyof RolePermissions): boolean {
  const userRole = getUserRole(user)
  const permissions = DEFAULT_ROLE_PERMISSIONS[userRole]
  return permissions[permission]
}

export function canAccessFeature(user: User | null, feature: string): boolean {
  switch (feature) {
    case 'user_management':
      return hasPermission(user, 'canManageUsers')
    case 'group_management':
      return hasPermission(user, 'canManageGroups')
    case 'curriculum_management':
      return hasPermission(user, 'canManageCurriculum')
    case 'event_management':
      return hasPermission(user, 'canManageEvents')
    case 'task_management':
      return hasPermission(user, 'canManageTasks')
    case 'analytics':
      return hasPermission(user, 'canViewAnalytics')
    case 'system_management':
      return hasPermission(user, 'canManageSystem')
    default:
      return false
  }
}

export function getRoleDisplayName(role: UserRole): string {
  switch (role) {
    case 'super_admin':
      return 'Super Administrator'
    case 'admin':
      return 'Administrator'
    case 'manager':
      return 'Group Manager'
    case 'member':
      return 'Group Member'
    case 'user':
      return 'User'
    default:
      return 'User'
  }
}

export function getRoleDescription(role: UserRole): string {
  switch (role) {
    case 'super_admin':
      return 'Full system access and control'
    case 'admin':
      return 'Administrative access to most features'
    case 'manager':
      return 'Can manage groups and their content'
    case 'member':
      return 'Can participate in group activities'
    case 'user':
      return 'Basic user access'
    default:
      return 'Basic user access'
  }
}

// Helper functions for role validation
export function canManage(role: UserRole): boolean {
  return hasRole(null, role)
}

export function canAdmin(role: UserRole): boolean {
  return role === 'admin' || role === 'super_admin'
}

export function isSuperAdmin(role: UserRole): boolean {
  return role === 'super_admin'
} 